/**
 * @fileoverview Boost card component for email subscription
 *
 * This component displays a promotional card with an email subscription form.
 * It's used in the blog section to encourage users to sign up for venture capital reports.
 *
 * @see {@link src/components/ui/auth/ExtendedAuthInput.tsx} - Input component
 * @see {@link src/components/ui/buttons/DefaultBtn.tsx} - Button component
 * @see {@link src/services/auth.service.ts} - Authentication service
 */

"use client";

import { Flex } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";

import ExtendedAuthInput from "@/components/ui/auth/ExtendedAuthInput";
import { ButtonDefault } from "@/components/ui/buttons/DefaultBtn";
import { RoslindaleText } from "@/components/ui/text/RoslindaleText";
import { useFreeAccountByEmailMutation } from "@/services/auth.service";
import { emailSchema } from "@/validations/formValidation";

/**
 * BoostCard component displays a promotional card with email subscription
 *
 * @see {@link src/components/ui/auth/ExtendedAuthInput.tsx} - Input component
 * @see {@link src/components/ui/buttons/DefaultBtn.tsx} - Button component
 * @see {@link src/services/auth.service.ts} - Authentication service
 */
/**
 * Props for the BoostCard component
 */
export interface BoostCardProps {
  /** Custom styles for the main container */
  style?: Record<string, string | number | Record<string, string | number>>;
  /** Custom styles for the first flex container */
  styleFlexFlex?: Record<
    string,
    string | number | Record<string, string | number>
  >;
  /** Custom styles for the second flex container */
  styleFlexFlexFlex?: Record<
    string,
    string | number | Record<string, string | number>
  >;
  /** Background image URL */
  backgroundImage?: string;
  /** Title text */
  title?: string;
  /** Button text */
  btnTitle: string;
  /** Custom button styles */
  customBtnStyle?: Record<
    string,
    string | number | Record<string, string | number>
  >;
  /** Whether to use minimal styling */
  isMin?: boolean;
  /** Max width of the container */
  maxW?: string | Record<string, string | number>;
}

export const BoostCard = ({
  style = {},
  styleFlexFlex = {},
  styleFlexFlexFlex = {},
  backgroundImage = 'url("/images/aVenture.png")',
  title = "Boost your research with aVenture's venture capital reports",
  btnTitle = "Reserve Your Spot",
  customBtnStyle = {},
  isMin = false,
  maxW,
}: BoostCardProps) => {
  const router = useRouter();
  const [freeAccountByEmail] = useFreeAccountByEmailMutation();
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  // Early return if registration is not required or explicitly disabled
  // Default to not showing the boost card (false) unless explicitly enabled
  if (
    process.env.NEXT_PUBLIC_REQUIRE_REGISTRATION !== "true" ||
    process.env.NEXT_PUBLIC_SHOW_BOOST_CARD !== "true"
  )
    return null;

  const blueBlockStyleImage = {
    backgroundImage: backgroundImage
      ? backgroundImage
      : 'url("/images/aVenture.png")',
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "start",
  };

  const handleReserve = ({ email }: { email: string }) => {
    if (email.trim()) {
      freeAccountByEmail({ email })
        .unwrap()
        .then(
          (res) =>
            res?.success &&
            router.push(
              btnTitle === "Subscribe"
                ? "/free-research?type=subscribe"
                : "/free-research"
            )
        );
    }
  };

  return (
    <Flex
      alignItems={"center"}
      maxW={"1057px"}
      bgColor={"mainBlue"}
      mx={{ md: "40px", lg: "152px" }}
      height={"274px"}
      style={blueBlockStyleImage}
      // mb={{ md: '78px', lg: '117px' }}
      borderRadius={{ md: "10px" }}
      p="17px 0px 21px 52px"
      {...style}
      backgroundImage='url("/images/cta_blog.png")'
    >
      <Flex
        maxW={maxW ? maxW : { base: "90%", desk: "50%" }}
        flexDir={isMin ? { base: "column", md: "row", lg: "column" } : "column"}
        justifyContent={{ base: "flex-start", smx: "space-between" }}
        gap={{ base: "24px", smx: "20px" }}
        height="100%"
        margin={isMin ? "auto" : "0"}
        {...styleFlexFlex}
      >
        <RoslindaleText
          customStyle={
            isMin
              ? {
                  fontSize: { base: "24px", smx: "32px" },
                  textAlign: "center",
                  color: "white",
                }
              : {
                  fontSize: { base: "24px", smx: "44px" },
                  textAlign: { base: "center", smx: "unset" },
                  color: "white",
                }
          }
        >
          {title}
        </RoslindaleText>

        <Flex
          flexDir={isMin ? "column" : { base: "column", smx: "row" }}
          alignItems="center"
          justifyContent={"center"}
          gap="8px"
          {...styleFlexFlexFlex}
          as="form"
          onSubmit={handleSubmit(handleReserve)}
        >
          <ExtendedAuthInput
            placeholder="Enter your email"
            {...{
              control,
              watch,
              type: "email",
              id: "email",
              boxStyles: {
                w: { base: "100%", md: isMin ? "100%" : "280px" },
              },
              styles: {
                h: "45px",
                color: "black",
                w: { base: "100%", md: isMin ? "100%" : "283px" },
                borderWidth: "1px",
                borderColor: "transparent",
              },
              errors,
              error_style: { bottom: "-10px" },
            }}
          />
          <ButtonDefault
            type="submit"
            hoverBgColor="darkRedNew"
            customStyles={{
              px: "24px",
              py: "8px",
              borderRadius: "4px",
              borderColor: "transparent",
              height: "45px",
              minW: { base: "100%", md: isMin ? "270px" : "170px" },
              ...customBtnStyle,
            }}
            backgroundColor={"red"}
            title={btnTitle || "Reserve Your Spot"}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
